import * as React from 'react';
import { Button } from '../components/Button';
import Page from '../components/Page';
import Loader from '../components/Loader';

import Appearance from './edit-guide/Appearance';
import BasicDetails from './edit-guide/BasicDetails';
import Dose from './edit-guide/Dose';
import StepList from './edit-guide/StepList';

import { GuideStep, StepType, Guide, GuideDose, getDose } from '../guide';
import { generateStep } from '../guides/generate';
import { getHeaders, useRequestAsync } from '../api/client';
import { ProfileContext, ProfileManager } from '../profile';
import { ErrorFlash, SuccessFlash } from '../components/Flash';
import { withState } from './OwnGuide';
import { Link } from 'react-router-dom';

type Props = {
  guide: Guide;
};

const getGuideSteps = (guide: Guide): GuideStep[] => {
  return guide.steps.filter(
    (item) => item.type !== StepType.boil && item.type !== StepType.grind,
  );
};

const combineGuideData = (
  guide: Guide,
  dose: GuideDose,
  steps: GuideStep[],
): Guide => {
  const coffeeStep = generateStep(StepType.grind, String(dose.coffee), 'g', 0);
  const waterStep = generateStep(StepType.boil, String(dose.water), 'ml', 0);

  const newSteps = steps.slice();
  newSteps.unshift(coffeeStep);
  newSteps.unshift(waterStep);

  let timeOffset = 0;

  return Object.assign({}, guide, {
    steps: newSteps.map(
      (step: GuideStep): GuideStep => {
        step.time_at = timeOffset;
        timeOffset += step.time_duration;
        return step;
      },
    ),
  });
};

const update = (context: ProfileManager, isNew: boolean) => {
  const pathName = (guide: Guide) => `/guides/${!isNew ? guide.id : 'new'}`;
  const methodName = (guide: Guide) => (!isNew ? 'PUT' : 'POST');

  return useRequestAsync((data: Guide) => ({
    url: pathName(data),
    method: methodName(data),
    body: JSON.stringify(data),
    headers: getHeaders(context),
  }));
};

const JumpLink = ({
  current = '',
  href = '',
  children,
  onClick,
  ...props
}: any) => (
  <button
    type="button"
    className={`f5 dim pv3 pa0 db black bn bg-transparent pointer outline-0 tl ${
      current === href ? 'b' : 'normal'
    }`}
    onClick={() => onClick(href)}
    {...props}
  >
    {children}
  </button>
);

const TickBox = ({ value }: any) =>
  value ? (
    <span className="tc dib w1 h1 bg-green br-100 b white pa1 lh-solid mr2">
      ✓
    </span>
  ) : null;

export function EditGuide(props: Props) {
  const isNew = props.guide.method === null;
  const [section, setSection] = React.useState('steps');
  const context = React.useContext(ProfileContext);
  const [state, submit] = update(context, isNew);
  const [guide, setGuide] = React.useState<Guide>(props.guide);
  const [dose, setDose] = React.useState<GuideDose>(getDose(guide));
  const [steps, setSteps] = React.useState<GuideStep[]>(getGuideSteps(guide));

  if (state.loading) {
    return <Loader />;
  }

  return (
    <Page
      title={guide.name || 'A brew guide'}
      subtitle={guide.description_text || guide.method}
    >
      {state.error && <ErrorFlash error={state.error} />}
      {state.complete && !state.error && (
        <SuccessFlash text="Guide has been saved" />
      )}
      <div className="flex justify-between flex-column flex-row-l">
        <div className="w-20-l w-100 flex flex-column pr3-l items-start">
          <Link to="/guides" className="f5 dim pv3 b black">
            ← Back to Brew Guides
          </Link>
          <JumpLink onClick={setSection} href="general" current={section}>
            <TickBox
              value={guide.name && guide.description_text && guide.method}
            />
            General
          </JumpLink>
          <JumpLink onClick={setSection} href="dose" current={section}>
            <TickBox
              value={
                guide.temperature &&
                guide.grind_size &&
                dose.water &&
                dose.coffee
              }
            />
            Initial Dose
          </JumpLink>
          <JumpLink onClick={setSection} href="steps" current={section}>
            <TickBox value={steps.length > 2} />
            Steps
          </JumpLink>
          <Button
            className="w-auto mv3 dim"
            onClick={() => submit(combineGuideData(guide, dose, steps))}
          >
            Save
          </Button>
        </div>
        <div className="w-80-l w-100 flex-auto">
          {section === 'general' && (
            <BasicDetails guide={guide} onChange={setGuide} />
          )}
          {section === 'dose' && (
            <Dose
              guide={guide}
              onChange={setGuide}
              dose={dose}
              onDoseChange={setDose}
            />
          )}
          {section === 'steps' && (
            <StepList
              guide={guide}
              dose={dose}
              steps={steps}
              onChange={setSteps}
            />
          )}
        </div>
      </div>
    </Page>
  );
}

export default withState(EditGuide);
